/**
 * Feedback Page
 *
 * User feedback form with emoji ratings, star ratings, file upload, and suggestions
 */

import { useState, useRef } from "react";
import { FeedbackService } from "../services/api/feedback";
import emoji1 from "../assets/svg/emoji-1.svg";
import emoji2 from "../assets/svg/emoji-2.svg";
import emoji3 from "../assets/svg/emoji-3.svg";
import emoji4 from "../assets/svg/emoji-4.svg";
import emoji5 from "../assets/svg/emoji-5.svg";
import uploadIcon from "../assets/svg/upload.svg";

const questions = [
  {
    id: "overall",
    type: "emoji" as const,
    question: "How would you rate your overall experience?",
  },
  {
    id: "design",
    type: "stars" as const,
    question: "How satisfied are you with the design and interface?",
  },
  {
    id: "intuitive",
    type: "stars" as const,
    question: "Was the platform easy to understand and use?",
  },
  {
    id: "rca",
    type: "stars" as const,
    question: "Was the RCA generation accurate and helpful?",
  },
];

export function Feedback() {
  const [overall, setOverall] = useState<number | null>(null); // 0..4 for emoji
  const [ratings, setRatings] = useState<Record<string, number | null>>(() =>
    questions.reduce(
      (acc, q) => {
        if (q.type === "stars") acc[q.id] = null;
        return acc;
      },
      {} as Record<string, number | null>,
    ),
  );

  const [suggestions, setSuggestions] = useState<string>("");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitMessage, setSubmitMessage] = useState<string>("");
  const [submitted, setSubmitted] = useState<boolean>(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const emojiImages = [emoji1, emoji2, emoji3, emoji4, emoji5];

  const resetForm = () => {
    setOverall(null);
    setRatings(
      questions.reduce(
        (acc, q) => {
          if (q.type === "stars") acc[q.id] = null;
          return acc;
        },
        {} as Record<string, number | null>,
      ),
    );
    setSuggestions("");
    setUploadedFile(null);
    setSubmitMessage("");
    setSubmitted(false);
  };

  const setStarRating = (id: string, value: number) => {
    setRatings((prev) => ({ ...prev, [id]: value }));
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (file) {
      if (file.type !== "application/pdf") {
        setSubmitMessage("Please upload only PDF files");
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setSubmitMessage("File size must be less than 10MB");
        return;
      }
      setUploadedFile(file);
      setSubmitMessage("");
    }
  };

  const buildSubmissionPayload = async () => {
    // Extension converts: emoji (0-4) -> (index + 1) = 1-5
    // Extension converts: stars (1-5) -> value * 2 = 2,4,6,8,10
    // BUT extension backend expects 1-10, so we need to match that format

    const payload: any = {};

    // Convert emoji rating (0-4) to 1-5 for backend
    if (overall !== null) {
      payload.overall = overall + 1; // 0->1, 1->2, 2->3, 3->4, 4->5
    }

    // Convert star ratings (1-5) to 1-5 for backend
    // Extension multiplies by 2, but let's check the backend expectation
    Object.entries(ratings).forEach(([k, v]) => {
      if (v !== null) {
        payload[k] = v; // Keep 1-5 range
      }
    });

    // Add suggestions if provided
    if (suggestions.trim()) {
      payload.suggestions = suggestions.trim();
    } else {
      payload.suggestions = null;
    }

    // Add file if uploaded
    if (uploadedFile) {
      payload.runbook = await FeedbackService.fileToBase64(uploadedFile);
    } else {
      payload.runbook = null;
    }

    return payload;
  };

  const handleSubmit = async () => {
    // Validation: check if all ratings are provided
    if (overall === null) {
      setSubmitMessage("Please complete the overall experience rating");
      return;
    }

    // Check if all star questions are answered
    const allStarsAnswered = Object.values(ratings).every((v) => v !== null);
    if (!allStarsAnswered) {
      setSubmitMessage("Please complete all rating questions");
      return;
    }

    // Clear any previous error messages
    setSubmitMessage("");
    setIsSubmitting(true);

    try {
      const payload = await buildSubmissionPayload();

      // Validate feedback data
      const validation = FeedbackService.validateFeedback(payload);
      if (!validation.isValid) {
        setSubmitMessage(validation.errors.join(", "));
        setIsSubmitting(false);
        return;
      }

      // Submit to backend API
      const response = await FeedbackService.submitFeedback(payload);

      console.log("Feedback submitted successfully:", response);
      setSubmitted(true);
      setSubmitMessage("Feedback submitted successfully!");
    } catch (err: any) {
      console.error("Failed to submit feedback:", err);
      setSubmitMessage(
        err.message || "Failed to submit feedback. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const prettyStars = (count: number | null) => {
    const arr: boolean[] = [];
    for (let i = 1; i <= 5; i++) arr.push(!!(count && count >= i));
    return arr;
  };

  if (submitted) {
    return (
      <div className="w-full h-full flex items-center justify-center p-8 bg-[#FAF6F1] dark:bg-[#1A2D47] relative">
        {/* Close button in top-right */}
        <button
          onClick={resetForm}
          className="absolute top-6 right-6 w-10 h-10 bg-[#FDFAF7] dark:bg-[#2C405F] border border-[#E5DDD3] dark:border-[#455D7D] rounded-full flex items-center justify-center hover:bg-[#F5E9D7] dark:hover:bg-[#455D7D] transition-colors shadow-md"
        >
          <svg
            className="w-5 h-5 text-[#1D1B19] dark:text-[#E3E6EA]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div className="max-w-md w-full bg-[#FDFAF7] dark:bg-[#2C405F] border border-[#E5DDD3] dark:border-[#455D7D] rounded-[16px] p-8 text-center">
          <div className="w-16 h-16 bg-[#6BCF7F] rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={3}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-semibold text-[#1D1B19] dark:text-[#E3E6EA] mb-2">
            Thank You!
          </h2>
          <p className="text-[#8E8378] dark:text-[#9AAABF] text-sm mb-6">
            Your feedback has been submitted successfully. We appreciate you
            taking the time to help us improve DuneWorks.
          </p>
          <button
            onClick={resetForm}
            className="w-full h-12 bg-[#1A2D47] dark:bg-[#6E829E] rounded-[12px] font-medium text-base text-[#FCFBFA] dark:text-[#0A1423] hover:bg-[#2C405F] dark:hover:bg-[#9AAABF] transition-all"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto p-6 pb-6">
          {/* Title */}
          <div className="mb-6">
            <h1 className="text-[#1D1B19] dark:text-[#E3E6EA] text-3xl font-semibold mb-2">
              Share Your Feedback
            </h1>
            <p className="text-[#8E8378] dark:text-[#9AAABF] text-base">
              We&apos;d love to hear your thoughts about Dune AI.
            </p>
          </div>

          {/* Questions Card */}
          <div className="bg-[#FDFAF7] dark:bg-[#2C405F] border border-[#E5DDD3] dark:border-[#455D7D] rounded-[16px] p-6 mb-4">
            {questions.map((q, idx) => {
              const isEmoji = q.type === "emoji";
              const current = ratings[q.id] ?? null;

              return (
                <div key={q.id}>
                  {idx > 0 && (
                    <div className="h-[1px] bg-[#E5DDD3] dark:bg-[#455D7D] my-6" />
                  )}

                  {/* Question Label */}
                  <p className="text-[#1D1B19] dark:text-[#E3E6EA] text-base font-medium mb-4">
                    {q.question}
                  </p>

                  {/* Emoji Type */}
                  {isEmoji ? (
                    <div className="w-full bg-[#FAF6F1] dark:bg-[#122135] rounded-[24px] flex justify-around items-center p-3">
                      {emojiImages.map((src, i) => {
                        const selected = overall === i;
                        return (
                          <div
                            key={i}
                            onClick={() => setOverall(i)}
                            className={`
                              h-14 w-14 rounded-[16px] flex items-center justify-center
                              cursor-pointer transition-all flex-shrink-0
                              ${
                                selected
                                  ? "bg-[#E5DDD3] dark:bg-[#455D7D] ring-2 ring-[#2C405F] dark:ring-[#6E829E]"
                                  : "bg-transparent hover:bg-[#FCFBFA] dark:hover:bg-[#2C405F]"
                              }
                            `}
                          >
                            <img
                              src={src}
                              alt={`Rating ${i + 1}`}
                              className="h-8 w-8"
                            />
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    // Stars Type
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {prettyStars(current).map((filled, i) => {
                          const starIndex = i + 1;
                          return (
                            <button
                              key={i}
                              onClick={() => setStarRating(q.id, starIndex)}
                              title={`${starIndex} star`}
                              className="cursor-pointer p-1 hover:scale-110 transition-transform"
                              aria-label={`${starIndex} star`}
                            >
                              {filled ? (
                                <svg
                                  className="w-6 h-6 text-[#F7C948]"
                                  viewBox="0 0 24 24"
                                  fill="currentColor"
                                >
                                  <path d="M12 .587l3.668 7.431L23.5 9.748l-5.75 5.602L19.333 24 12 19.897 4.667 24l1.583-8.65L.5 9.748l7.832-1.73L12 .587z" />
                                </svg>
                              ) : (
                                <svg
                                  className="w-6 h-6 text-[#B7AB9E]"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                >
                                  <path
                                    d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.62L12 2 9.19 8.62 2 9.24l5.46 4.73L5.82 21z"
                                    strokeWidth="1.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              )}
                            </button>
                          );
                        })}
                      </div>
                      <span className="text-[#8E8378] dark:text-[#9AAABF] text-sm font-medium min-w-[80px] text-right">
                        {current ? `${current} / 5` : "Not rated"}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Upload Block */}
          <div className="bg-[#FDFAF7] dark:bg-[#2C405F] border border-[#E5DDD3] dark:border-[#455D7D] rounded-[16px] p-6 mb-4">
            <p className="text-[#1D1B19] dark:text-[#E3E6EA] text-base font-medium mb-4">
              Upload your log or runbook (optional)
            </p>

            <div
              className="w-full bg-[#FAF6F1] dark:bg-[#122135] border-2 border-dashed border-[#E5DDD3] dark:border-[#455D7D] rounded-[12px] p-8 flex flex-col items-center justify-center cursor-pointer hover:bg-[#FCFBFA] dark:hover:bg-[#1A2D47] transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              {uploadedFile ? (
                <div className="text-center">
                  <div className="w-12 h-12 bg-[#E5DDD3] dark:bg-[#455D7D] rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg
                      className="w-6 h-6 text-[#1A2D47] dark:text-[#E3E6EA]"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                    </svg>
                  </div>
                  <p className="text-[#1D1B19] dark:text-[#E3E6EA] text-sm font-medium mb-1">
                    {uploadedFile.name}
                  </p>
                  <p className="text-[#8E8378] dark:text-[#9AAABF] text-xs mb-3">
                    {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setUploadedFile(null);
                    }}
                    className="text-red-500 text-sm hover:underline"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <>
                  <img
                    src={uploadIcon}
                    alt="Upload"
                    className="w-10 h-10 mb-3 opacity-60"
                  />
                  <p className="text-[#1D1B19] dark:text-[#E3E6EA] text-sm font-medium mb-1">
                    Drag and drop or click to upload
                  </p>
                  <p className="text-[#8E8378] dark:text-[#9AAABF] text-xs">
                    PDF only (max 10MB)
                  </p>
                </>
              )}
              <input
                type="file"
                ref={fileInputRef}
                accept=".pdf"
                onChange={handleFileUpload}
                className="hidden"
              />
            </div>
          </div>

          {/* Suggestions */}
          <div className="bg-[#FDFAF7] dark:bg-[#2C405F] border border-[#E5DDD3] dark:border-[#455D7D] rounded-[16px] p-6 mb-4">
            <p className="text-[#1D1B19] dark:text-[#E3E6EA] text-base font-medium mb-4">
              Any suggestions or comments? (optional)
            </p>

            <textarea
              className="w-full h-32 bg-[#FAF6F1] dark:bg-[#122135] border border-[#E5DDD3] dark:border-[#455D7D] rounded-[12px] p-4 resize-none text-sm text-[#1D1B19] dark:text-[#E3E6EA] placeholder:text-[#8E8378] dark:placeholder:text-[#9AAABF] outline-none focus:ring-2 focus:ring-[#1A2D47] dark:focus:ring-[#6E829E] transition-all"
              value={suggestions}
              onChange={(e) => {
                setSuggestions(e.target.value);
                if (submitMessage) setSubmitMessage("");
              }}
              placeholder="Tell us what can be improved..."
              aria-label="Suggestions or comments"
            />
          </div>
        </div>
      </div>

      {/* Fixed Submit Footer */}
      <div className="flex-shrink-0 border-t border-[#E5DDD3] bg-[#FDFAF7] p-4 shadow-lg">
        <div className="max-w-3xl mx-auto">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="w-full h-12 bg-[#1A2D47] rounded-[12px] font-medium text-base text-[#FCFBFA] flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed hover:bg-[#2C405F] transition-all"
          >
            {isSubmitting ? "Submitting..." : "Submit Feedback"}
          </button>

          {submitMessage && (
            <p
              className={`text-sm mt-3 text-center ${
                submitMessage.includes("success")
                  ? "text-green-600"
                  : "text-red-500"
              }`}
            >
              {submitMessage}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
